import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import {
  ClipboardList,
  FileText,
  Activity,
  Building2,
  ShieldCheck,
  GripVertical,
  Search,
  PlusCircle,
  History,
  FolderOpen,
  PlayCircle,
  Briefcase,
  Undo2,
  Tent,
  Video,
  CalendarDays,
  MessageCircle,
  ClipboardSignature,
  PhoneCall,
  FileBarChart2,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import CitizenSearchBar from '../components/CitizenSearchBar';
import { useCitizen } from '../CitizenContext';
import http from '../services/http';

const r = (...arr) => arr.map((s) => s.toLowerCase());

function tenantBase() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return m ? m[0] : '';
  } catch {
    return '';
  }
}

function tenantize(path) {
  const base = tenantBase();
  const p = String(path || '');
  if (!base) return p;
  if (!p.startsWith('/')) return `${base}/${p}`.replace(/\/{2,}/g, '/');
  if (p.startsWith(base)) return p;
  return `${base}${p}`.replace(/\/{2,}/g, '/');
}

function normalizeList(data) {
  return Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.visits)
            ? data.visits
            : [];
}

function getVisitStatus(visit) {
  const raw = visit?.prescription?.status || visit?.status || '';
  return String(raw || '').toLowerCase();
}

function getVisitId(visit) {
  return visit?.visitId || visit?._id || '';
}

function getVisitCitizenId(visit) {
  const raw =
    visit?.citizenId ||
    visit?.citizen?.citizenId ||
    visit?.citizen?.id ||
    visit?.citizen?._id ||
    visit?.citizen ||
    visit?.cid ||
    visit?.citizenDetails?.citizenId ||
    visit?.citizenRef?.citizenId ||
    '';

  if (raw && typeof raw === 'object') {
    return String(raw.citizenId || raw.id || raw._id || '').trim();
  }
  return String(raw || '').trim();
}

function visitBelongsToCitizen(visit, citizenId) {
  const target = String(citizenId || '').trim();
  if (!target) return false;
  return getVisitCitizenId(visit) === target;
}

function getVisitTimestamp(visit) {
  return new Date(
    visit?.visitDate ||
    visit?.date ||
    visit?.updatedAt ||
    visit?.createdAt ||
    0
  ).getTime();
}

function isContinueEligibleVisit(visit) {
  const status = getVisitStatus(visit);
  return ['draft', 'pending', 'returned'].includes(status);
}

function isInFlightVisit(visit) {
  const status = getVisitStatus(visit);
  return ['draft', 'pending', 'submitted', 'returned'].includes(status);
}

function isHistoricalVisit(visit) {
  const status = getVisitStatus(visit);
  if (!status) return true; // preserve legacy records that pre-date explicit workflow status
  return !isInFlightVisit(visit);
}

function routeExistsInMarkup(routePath) {
  try {
    const html = document?.body?.innerHTML || '';
    return html.includes(routePath);
  } catch {
    return false;
  }
}

function persistCitizenContext(citizen) {
  if (!citizen?.citizenId) return;
  try {
    localStorage.setItem('activeCitizen', JSON.stringify(citizen));
    localStorage.setItem('activeCitizenId', citizen.citizenId);
    if (citizen?.centre) {
      localStorage.setItem('activeCentre', citizen.centre);
      localStorage.setItem('centre', citizen.centre);
    }
  } catch {}
}

function clearCitizenContext(citizenId) {
  try {
    localStorage.removeItem('activeCitizen');
    localStorage.removeItem('activeCitizenId');
    localStorage.removeItem('selectedCitizen');
    localStorage.removeItem('activeCentre');
    localStorage.removeItem('centre');
    localStorage.removeItem('activeVisitId');
    localStorage.removeItem('visitId');
    localStorage.removeItem('editReturnedVisit');
    if (citizenId) {
      localStorage.removeItem(`activeVisitId:${citizenId}`);
    }
  } catch {}
}

function normalizeCentreLabel(value) {
  const raw = String(value || '').trim();
  const v = raw.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

  if (!v) return '';
  if (v.includes('keru')) return 'CRHA Keru';
  if (v.includes('dhawa')) return 'CRHA Dhawa';
  if (v.includes('pratap')) return 'UHTC Pratap Nagar';
  if (v.includes('dundhara') || v.includes('dhundhara') || v.includes('dhundara') || v.includes('dundara')) {
    return 'CRHA Dhundhara';
  }
  return raw;
}

function getCitizenDisplayName(citizen) {
  return citizen?.name || citizen?.fullName || citizen?.citizenName || 'Selected Citizen';
}

function getCitizenDisplayCentre(citizen, visit) {
  return normalizeCentreLabel(visit?.centre || citizen?.centre || '');
}

function getVisitStatusLabel(visit) {
  const status = getVisitStatus(visit);
  if (!status) return 'No saved visit yet';
  if (status === 'draft') return 'Draft visit available';
  if (status === 'pending') return 'Pending approval / incomplete flow';
  if (status === 'returned') return 'Returned for editing';
  if (status === 'approved') return 'Latest visit approved';
  return status;
}

const SECTION_META = {
  citizen: {
    title: 'Citizen Services',
    titleHi: 'नागरिक सेवाएँ',
    accent: 'text-emerald-800 dark:text-emerald-200',
    dragClass: 'text-emerald-700 dark:text-emerald-200',
    description: 'Search, select, register, continue, and document citizen visit workflow from one place.',
  },
  activities: {
    title: 'Activities & Clinics',
    titleHi: 'गतिविधियाँ और क्लिनिक्स',
    accent: 'text-violet-800 dark:text-violet-200',
    dragClass: 'text-violet-700 dark:text-violet-200',
    description: 'Launch guided activities, review own work history, and access activity-linked reports.',
  },
  telemedicine: {
    title: 'Telemedicine',
    titleHi: 'टेलीमेडिसिन',
    accent: 'text-cyan-800 dark:text-cyan-200',
    dragClass: 'text-cyan-700 dark:text-cyan-200',
    description: 'Access teleconsultation, appointments, consent, and support tools without mixing them into OPD flow.',
  },
  operations: {
    title: 'Centre / Operations',
    titleHi: 'केंद्र / संचालन',
    accent: 'text-orange-800 dark:text-orange-200',
    dragClass: 'text-orange-700 dark:text-orange-200',
    description: 'Keep census, assigned duties, centre services, and operations tools together as a separate reporting lane.',
  },
};

const baseSections = {
  citizen: [
    { label: 'Search / Select Citizen', labelHi: 'नागरिक खोजें / चुनें', icon: <Search size={32} />, path: '__search__', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin', 'deo') },
    { label: 'Add New Citizen', labelHi: 'नया नागरिक जोड़ें', icon: <PlusCircle size={32} />, path: '/dashboard/add-citizen', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin') },
    { label: 'Add Visit', labelHi: 'नई विज़िट जोड़ें', icon: <PlusCircle size={32} />, path: '__add_visit__', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin', 'deo') },
    { label: 'Previous Visits', labelHi: 'पिछली विज़िट्स', icon: <History size={32} />, path: '__previous_visits__', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin', 'deo') },
    { label: 'Upload Documents', labelHi: 'दस्तावेज़ अपलोड करें', icon: <FolderOpen size={32} />, path: '__upload_documents__', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin', 'deo') },
    { label: 'Social & Household Context', labelHi: 'सामाजिक एवं घरेलू विवरण', icon: <FileText size={32} />, path: '__social_household__', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin', 'deo') },
    { label: 'Continue Current Visit', labelHi: 'वर्तमान विज़िट जारी रखें', icon: <ClipboardList size={32} />, path: '__continue_visit__', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin', 'deo') },
  ],
  activities: [
    { label: 'Launch Activity', labelHi: 'गतिविधि प्रारंभ करें', icon: <PlayCircle size={32} />, path: '/intern/activity/new', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin') },
    { label: 'My Activities', labelHi: 'मेरी गतिविधियाँ', icon: <Activity size={32} />, path: '/intern/activity', roles: r('intern') },
    { label: 'My Work', labelHi: 'मेरा कार्य', icon: <Briefcase size={32} />, path: '/dashboard/approval', roles: r('faculty', 'junior resident', 'senior resident', 'admin') },
    { label: 'Returned OPD Visits', labelHi: 'सुधार हेतु लौटाई गई ओपीडी विज़िट्स', icon: <Undo2 size={32} />, path: '/dashboard/approval?tab=returned', roles: r('intern') },
    { label: 'Returned Guided Activities', labelHi: 'सुधार हेतु लौटाई गई गतिविधियाँ', icon: <Undo2 size={32} />, path: '/intern/activity/returned', roles: r('intern') },
    { label: 'Medical Camp / Outreach', labelHi: 'मेडिकल कैंप / आउटरीच', icon: <Tent size={32} />, path: '/dashboard/clinical/camps', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin') },
    { label: 'Clinical Services Report', labelHi: 'क्लिनिकल सेवा रिपोर्ट', icon: <FileBarChart2 size={32} />, path: '/dashboard/clinical/report', roles: r('faculty', 'junior resident', 'senior resident', 'admin') },
  ],
  telemedicine: [
    { label: 'Start Teleconsultation', labelHi: 'परामर्श प्रारंभ', icon: <Video size={32} />, path: '/dashboard/telemedicine/start-teleconsultation', roles: r('junior resident', 'senior resident', 'faculty') },
    { label: 'Appointment Schedule', labelHi: 'नियुक्तियाँ', icon: <CalendarDays size={32} />, path: '/dashboard/telemedicine/appointments', roles: r('intern', 'junior resident', 'senior resident', 'faculty') },
    { label: 'Chat History', labelHi: 'चैट इतिहास', icon: <MessageCircle size={32} />, path: '/dashboard/telemedicine/chat-history', roles: r('junior resident', 'senior resident', 'faculty') },
    { label: 'Consent Forms', labelHi: 'सहमति पत्र', icon: <ClipboardSignature size={32} />, path: '/dashboard/telemedicine/consent-forms', roles: r('intern', 'junior resident', 'senior resident', 'faculty') },
    { label: 'Helpline Support', labelHi: 'हेल्पलाइन सहायता', icon: <PhoneCall size={32} />, path: '/dashboard/telemedicine/helpline-support', roles: r('intern', 'junior resident', 'senior resident', 'faculty', 'citizen') },
  ],
  operations: [
    { label: 'Daily OPD Census', labelHi: 'ओपीडी गणना', icon: <ClipboardList size={32} />, path: '/dashboard/operations/daily-opd-census', roles: r('faculty', 'junior resident', 'senior resident', 'admin') },
    { label: 'Assigned Duties', labelHi: 'निर्धारित कर्तव्य', icon: <FileText size={32} />, path: '/assigned-duties', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin') },
    { label: 'Intern Assessment', labelHi: 'इंटर्न मूल्यांकन', icon: <Activity size={32} />, path: '/intern-assessment', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin') },
    { label: 'Centre Services Hub', labelHi: 'स्वास्थ्य केंद्र सेवाएँ हब', icon: <Building2 size={32} />, path: '/dashboard/operations/chc', roles: r('faculty', 'admin', 'junior resident', 'senior resident', 'medical officer', 'lab technician', 'radiographer', 'pharmacist', 'ayush', 'ayush medical officer', 'deo') },
    { label: 'Emergency Module', labelHi: 'आपातकालीन मॉड्यूल', icon: <ShieldCheck size={32} />, path: '/emergency-module', roles: r('faculty', 'intern', 'junior resident', 'senior resident', 'admin') },
  ],
};

function SortableCard({ item, dragClass, onClick, disabled = false }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.path });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : 'auto',
    opacity: disabled ? 0.55 : (isDragging ? 0.7 : 1),
  };

  const content = (
    <Card className="rounded-2xl shadow-md hover:shadow-xl bg-white dark:bg-[#26272b] border border-gray-200 dark:border-[#363640] transition-colors duration-300 ease-in-out cursor-pointer h-full">
      <CardContent className="flex flex-col items-center justify-center py-8 relative h-full">
        <span {...attributes} {...listeners} className="absolute top-2 right-2 cursor-grab active:cursor-grabbing" title="Drag to reorder">
          <GripVertical size={20} className={`${dragClass} opacity-70`} />
        </span>
        <div className="mb-3 mt-1 transition-colors">{item.icon}</div>
        <Typography variant="h6" align="center" className="text-gray-800 dark:text-gray-100 transition-colors">
          {item.label} / {item.labelHi}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <div ref={setNodeRef} style={style}>
      {disabled ? (
        <div>{content}</div>
      ) : onClick ? (
        <button type="button" onClick={onClick} className="w-full text-left no-underline bg-transparent border-0 p-0">
          {content}
        </button>
      ) : (
        <Link to={tenantize(item.path)} className="no-underline block h-full">
          {content}
        </Link>
      )}
    </div>
  );
}

function useOrderedSection(sectionKey, visibleItems, role) {
  const [items, setItems] = useState(visibleItems);

  useEffect(() => {
    setItems(visibleItems);
  }, [visibleItems]);

  useEffect(() => {
    const storageKey = `group_operations_${sectionKey}_order_${role}`;
    const legacyKey = sectionKey === 'operations' ? `group_operations_order_${role}` : null;
    let alive = true;

    fetch(`/api/dashboard-order?group=${encodeURIComponent(`operations-${sectionKey}`)}&role=${encodeURIComponent(role)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!alive) return;
        if (data && Array.isArray(data.order)) {
          const map = Object.fromEntries(visibleItems.map((s) => [s.path, s]));
          const ordered = data.order.map((p) => map[p]).filter(Boolean);
          const extras = visibleItems.filter((s) => !data.order.includes(s.path));
          setItems([...ordered, ...extras]);
          return;
        }

        const raw = localStorage.getItem(storageKey) || (legacyKey ? localStorage.getItem(legacyKey) : null);
        if (!raw) return;
        try {
          const arr = JSON.parse(raw);
          if (Array.isArray(arr)) {
            const map = Object.fromEntries(visibleItems.map((s) => [s.path, s]));
            setItems([
              ...arr.map((p) => map[p]).filter(Boolean),
              ...visibleItems.filter((s) => !arr.includes(s.path)),
            ]);
          }
        } catch {
          setItems(visibleItems);
        }
      })
      .catch(() => {
        const raw = localStorage.getItem(storageKey) || (legacyKey ? localStorage.getItem(legacyKey) : null);
        if (!raw) return;
        try {
          const arr = JSON.parse(raw);
          if (Array.isArray(arr)) {
            const map = Object.fromEntries(visibleItems.map((s) => [s.path, s]));
            setItems([
              ...arr.map((p) => map[p]).filter(Boolean),
              ...visibleItems.filter((s) => !arr.includes(s.path)),
            ]);
          }
        } catch {}
      });

    return () => { alive = false; };
  }, [sectionKey, role, visibleItems]);

  const persist = (newOrder) => {
    const orderPaths = newOrder.map((sec) => sec.path);
    setItems(newOrder);
    const storageKey = `group_operations_${sectionKey}_order_${role}`;
    localStorage.setItem(storageKey, JSON.stringify(orderPaths));
    if (sectionKey === 'operations') {
      localStorage.setItem(`group_operations_order_${role}`, JSON.stringify(orderPaths));
    }
    fetch('/api/dashboard-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ group: `operations-${sectionKey}`, role, order: orderPaths }),
    }).catch(() => {});
  };

  return [items, persist];
}

function SectionGrid({ sectionKey, items, persist, renderItem }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((sec) => sec.path === active.id);
    const newIndex = items.findIndex((sec) => sec.path === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    persist(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((s) => s.path)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {items.map((item) => renderItem(item, SECTION_META[sectionKey].dragClass))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default function GroupOperations() {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateToFirstAvailable = useCallback((paths = []) => {
    const candidates = Array.isArray(paths) ? paths.filter(Boolean) : [paths].filter(Boolean);
    if (!candidates.length) return;

    const preferred = candidates.find((path) => routeExistsInMarkup(path)) || candidates[0];
    navigate(tenantize(preferred));
  }, [navigate]);

  const user = JSON.parse(localStorage.getItem('loggedInUser') || 'null') || {};
  const role = String(user?.role || user?.userType || '').toLowerCase();
  const { citizen, setCitizen } = useCitizen();
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);

  const visibleCitizen = useMemo(
    () => baseSections.citizen.filter((section) => section.roles.includes(role) || role === 'admin' || role === 'faculty'),
    [role]
  );
  const visibleActivities = useMemo(
    () => baseSections.activities.filter((section) => section.roles.includes(role) || role === 'admin' || role === 'faculty'),
    [role]
  );
  const visibleTelemedicine = useMemo(
    () => baseSections.telemedicine.filter((section) => section.roles.includes(role) || role === 'admin' || role === 'faculty'),
    [role]
  );
  const visibleOperations = useMemo(
    () => baseSections.operations.filter((section) => section.roles.includes(role) || role === 'admin' || role === 'faculty'),
    [role]
  );

  const [citizenItems, persistCitizen] = useOrderedSection('citizen', visibleCitizen, role);
  const [activityItems, persistActivities] = useOrderedSection('activities', visibleActivities, role);
  const [telemedicineItems, persistTelemedicine] = useOrderedSection('telemedicine', visibleTelemedicine, role);
  const [operationsItems, persistOperations] = useOrderedSection('operations', visibleOperations, role);

  useEffect(() => {
    const stateCitizen = location?.state?.citizen;
    const stateCitizenId = location?.state?.citizenId || '';
    let alive = true;

    if (stateCitizen?.citizenId) {
      setCitizen(stateCitizen);
      persistCitizenContext(stateCitizen);
      return undefined;
    }

    if (citizen?.citizenId) {
      persistCitizenContext(citizen);
      return undefined;
    }

    if (stateCitizenId) {
      http.get(`/api/citizens/${encodeURIComponent(stateCitizenId)}`)
        .then(({ data }) => {
          if (!alive || !data?.citizenId) return;
          setCitizen(data);
          persistCitizenContext(data);
        })
        .catch(() => {});
      return () => { alive = false; };
    }

    try {
      const cached = JSON.parse(localStorage.getItem('activeCitizen') || 'null');
      if (cached?.citizenId) {
        setCitizen(cached);
        persistCitizenContext(cached);
      }
    } catch {}

    return () => { alive = false; };
  }, [citizen?.citizenId, location?.state, setCitizen]);

  useEffect(() => {
    if (!citizen?.citizenId) {
      setVisits([]);
      return;
    }

    const ctrl = new AbortController();
    setVisitsLoading(true);

    const selectedCitizenId = String(citizen.citizenId || '').trim();
    const citizenId = encodeURIComponent(selectedCitizenId);
    const endpoints = [
      `/api/visits?citizenId=${citizenId}`,
      `/api/citizens/${citizenId}/visits`,
      `/api/visit-history?citizenId=${citizenId}`,
    ];

    (async () => {
      let nextVisits = [];
      for (const endpoint of endpoints) {
        try {
          const { data } = await http.get(endpoint, { signal: ctrl.signal });
          const list = normalizeList(data);
          nextVisits = list.filter((visit) => visitBelongsToCitizen(visit, selectedCitizenId));
          // A successful empty response is authoritative: this citizen has no Visits.
          // Do not fall through to a looser endpoint and accidentally adopt tenant-wide data.
          break;
        } catch (err) {
          if (ctrl.signal.aborted) return;
        }
      }

      if (ctrl.signal.aborted) return;

      setVisits(
        nextVisits
          .slice()
          .sort((a, b) => getVisitTimestamp(b) - getVisitTimestamp(a))
      );
      setVisitsLoading(false);
    })().catch(() => {
      if (!ctrl.signal.aborted) {
        setVisits([]);
        setVisitsLoading(false);
      }
    });

    return () => ctrl.abort();
  }, [citizen?.citizenId]);

  const latestVisit = visits[0] || null;
  const editableVisit = useMemo(
    () => visits.find((visit) => isContinueEligibleVisit(visit)) || null,
    [visits]
  );
  const historicalVisits = useMemo(
    () => visits.filter((visit) => isHistoricalVisit(visit)),
    [visits]
  );
  const socialContext = citizen?.socialHouseholdContext || {};
  const socialContextComplete = socialContext?.completionStatus === 'complete';
  const showSocialContextReminder = historicalVisits.length >= 2 && !socialContextComplete;

  const citizenDisplayName = useMemo(() => getCitizenDisplayName(citizen), [citizen]);
  const citizenDisplayCentre = useMemo(
    () => getCitizenDisplayCentre(citizen, editableVisit || latestVisit),
    [citizen, editableVisit, latestVisit]
  );
  const latestVisitStatusLabel = useMemo(
    () => getVisitStatusLabel(editableVisit || latestVisit),
    [editableVisit, latestVisit]
  );
  const citizenSummaryCards = useMemo(() => {
    if (!citizen?.citizenId) return [];
    return [
      { label: 'Selected citizen', value: citizenDisplayName },
      { label: 'Citizen ID', value: citizen.citizenId },
      { label: 'Previous visits', value: String(historicalVisits.length) },
      { label: 'Current workflow status', value: latestVisitStatusLabel },
    ];
  }, [citizen?.citizenId, citizenDisplayName, historicalVisits.length, latestVisitStatusLabel]);

  const goAddVisit = () => {
    if (!citizen?.citizenId) return;
    persistCitizenContext(citizen);
    navigate(tenantize(`/citizen/${encodeURIComponent(citizen.citizenId)}/add-visit`));
  };

  const goPreviousVisits = () => {
    if (!citizen?.citizenId) return;
    persistCitizenContext(citizen);
    navigate(tenantize(`/citizen/${encodeURIComponent(citizen.citizenId)}/previous-visits`), { state: { citizenId: citizen.citizenId } });
  };

  const goUploadDocuments = () => {
    if (!citizen?.citizenId) return;
    persistCitizenContext(citizen);
    navigate(tenantize(`/citizen/${encodeURIComponent(citizen.citizenId)}/upload-documents`));
  };

  const goSocialHousehold = () => {
    if (!citizen?.citizenId) return;
    persistCitizenContext(citizen);
    navigate(tenantize(`/citizen/${encodeURIComponent(citizen.citizenId)}/social-household-context`));
  };

  const goContinueVisit = () => {
    if (!citizen?.citizenId) return;

    const selectedCitizenId = String(citizen.citizenId || '').trim();
    const scopedActiveVisitId = localStorage.getItem(`activeVisitId:${selectedCitizenId}`) || localStorage.getItem('activeVisitId') || '';
    const activeVisitFromList = visits.find(
      (visit) =>
        visitBelongsToCitizen(visit, selectedCitizenId) &&
        String(getVisitId(visit)) === String(scopedActiveVisitId)
    );
    const fallbackEligibleVisit = visits.find(
      (visit) => visitBelongsToCitizen(visit, selectedCitizenId) && isContinueEligibleVisit(visit)
    );
    const targetVisit =
      (
        activeVisitFromList &&
        visitBelongsToCitizen(activeVisitFromList, selectedCitizenId) &&
        isContinueEligibleVisit(activeVisitFromList)
          ? activeVisitFromList
          : null
      ) ||
      fallbackEligibleVisit ||
      null;

    if (!targetVisit) {
      goAddVisit();
      return;
    }

    const targetVisitId = getVisitId(targetVisit);
    const targetStatus = getVisitStatus(targetVisit);

    try {
      localStorage.setItem('activeVisitId', targetVisitId);
      localStorage.setItem(`activeVisitId:${citizen.citizenId}`, targetVisitId);
      if (targetStatus === 'returned') localStorage.setItem('editReturnedVisit', targetVisitId);
      else localStorage.removeItem('editReturnedVisit');

      const existingContext = (() => {
        try {
          return JSON.parse(localStorage.getItem(`activeVisitContext:${targetVisitId}`) || localStorage.getItem('activeVisitContext') || 'null');
        } catch {
          return null;
        }
      })();

      const nextContext = {
        ...(existingContext && typeof existingContext === 'object' ? existingContext : {}),
        visitId: targetVisitId,
        citizenId: citizen.citizenId,
        centre: targetVisit?.centre || citizen?.centre || existingContext?.centre || '',
        visitType: targetVisit?.visitType || existingContext?.visitType || '',
        visitCategory: targetVisit?.visitCategory || existingContext?.visitCategory || '',
        clinicalPathway: targetVisit?.clinicalPathway || existingContext?.clinicalPathway || '',
        complaintGroup: targetVisit?.complaintGroup || existingContext?.complaintGroup || '',
        presentingComplaint: targetVisit?.presentingComplaint || existingContext?.presentingComplaint || '',
      };

      localStorage.setItem('activeVisitContext', JSON.stringify(nextContext));
      localStorage.setItem(`activeVisitContext:${targetVisitId}`, JSON.stringify(nextContext));
      persistCitizenContext(citizen);
    } catch {}

    const qs = new URLSearchParams();
    qs.set('section', 'history');

    navigate(
      tenantize(`/citizen/${encodeURIComponent(citizen.citizenId)}/visit/${encodeURIComponent(targetVisitId)}/workspace?${qs.toString()}`),
      {
        state: {
          citizenId: citizen.citizenId,
          visitId: targetVisitId,
          resume: true,
          visitContext: {
            visitId: targetVisitId,
            citizenId: citizen.citizenId,
            centre: targetVisit?.centre || citizen?.centre || '',
            visitType: targetVisit?.visitType || '',
            visitCategory: targetVisit?.visitCategory || '',
            clinicalPathway: targetVisit?.clinicalPathway || '',
            complaintGroup: targetVisit?.complaintGroup || '',
            presentingComplaint: targetVisit?.presentingComplaint || '',
          },
        },
      }
    );
  };

  const activityAction = (item) => {
    switch (item.path) {
      case '/intern/activity/new':
        return () => navigateToFirstAvailable(['/intern/activity/new', '/dashboard/intern/activity/new']);
      case '/intern/activity':
        return () => navigateToFirstAvailable(['/intern/activity', '/dashboard/intern/activity']);
      case '/dashboard/approval':
        return () => navigateToFirstAvailable(['/dashboard/approval', '/dashboard/approvals', '/dashboard/approval-dashboard']);
      case '/dashboard/approval?tab=returned':
        return () => navigate(tenantize('/dashboard/approval?tab=returned'));
      case '/intern/activity/returned':
        return () => navigateToFirstAvailable(['/intern/activity/returned', '/dashboard/intern/activity/returned']);
      default:
        return undefined;
    }
  };

  const citizenAction = (item) => {
    switch (item.path) {
      case '__search__':
        return undefined;
      case '__add_visit__':
        return goAddVisit;
      case '__previous_visits__':
        return goPreviousVisits;
      case '__upload_documents__':
        return goUploadDocuments;
      case '__social_household__':
        return goSocialHousehold;
      case '__continue_visit__':
        return goContinueVisit;
      default:
        return undefined;
    }
  };

  const citizenDisabled = (item) => {
    if (item.path === '__search__' || item.path === '/dashboard/add-citizen') return false;
    return !citizen?.citizenId;
  };

  const clearSelection = () => {
    const citizenId = citizen?.citizenId;
    setCitizen(null);
    setVisits([]);
    clearCitizenContext(citizenId);
  };

  return (
    <div className="p-6 bg-white dark:bg-[#232326] rounded-xl shadow border border-gray-200 dark:border-[#363640] min-h-screen transition-colors space-y-8">
      <div>
        <Typography variant="h5" className="text-orange-800 dark:text-orange-200 font-semibold mb-1">
          Comprehensive Services Hub
        </Typography>
        <Typography variant="body2" className="text-gray-600 dark:text-gray-300">
          This is the main operational launch surface for citizen workflow, activities, telemedicine, and centre operations. New citizen registration and visit initiation should start here.
        </Typography>
      </div>

      {citizen?.citizenId ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {citizenSummaryCards.map((card) => (
            <div key={card.label} className="rounded-2xl border border-emerald-200 dark:border-emerald-900 bg-[#f9fffb] dark:bg-[#112017] p-4 shadow-sm">
              <Typography variant="caption" className="text-gray-600 dark:text-gray-400 block mb-1">
                {card.label}
              </Typography>
              <Typography variant="subtitle1" className="text-emerald-900 dark:text-emerald-100 font-semibold break-words">
                {card.value || '—'}
              </Typography>
            </div>
          ))}
        </div>
      ) : null}

      <section className="space-y-4">
        <div>
          <Typography variant="h6" className={`${SECTION_META.citizen.accent} font-semibold`}>
            {SECTION_META.citizen.title} / {SECTION_META.citizen.titleHi}
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-gray-300 mt-1">
            {SECTION_META.citizen.description}
          </Typography>
        </div>

        <div className="bg-[#f7fff9] dark:bg-[#132a1c] border border-emerald-200 dark:border-emerald-900 rounded-2xl p-4 space-y-4">
          <CitizenSearchBar
            onResult={(c) => {
              if (c?.citizenId) {
                setCitizen(c);
                persistCitizenContext(c);
              }
            }}
          />

          {citizen?.citizenId ? (
            <div className="rounded-xl border border-emerald-300 dark:border-emerald-800 bg-white dark:bg-[#0f1d14] p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2 mb-2 text-sm text-emerald-900 dark:text-emerald-200">
                <span className="font-semibold">Citizen:</span>
                <span>{citizenDisplayName}</span>
                <span className="opacity-70">•</span>
                <span className="font-mono">{citizen.citizenId}</span>
                {citizenDisplayCentre ? (
                  <>
                    <span className="opacity-70">•</span>
                    <span>{citizenDisplayCentre}</span>
                  </>
                ) : null}
              </div>

              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {visitsLoading ? 'Loading previous visits…' : `Previous visits found: ${historicalVisits.length}`}
                  {latestVisit ? ` | Latest visit: ${latestVisit.visitId || latestVisit._id || 'Available'}` : ''}
                  {!visitsLoading ? ` | ${latestVisitStatusLabel}` : ''}
                </div>

                <button
                  type="button"
                  onClick={clearSelection}
                  className="inline-flex items-center rounded-lg border border-emerald-300 dark:border-emerald-700 px-3 py-1.5 text-sm font-medium text-emerald-900 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950 transition-colors"
                >
                  Clear selection
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-emerald-300 dark:border-emerald-800 p-4 text-sm text-gray-600 dark:text-gray-300">
              Search and select a citizen here to enable visit-specific actions. Use Add New Citizen here for fresh registration.
            </div>
          )}

          {citizen?.citizenId && showSocialContextReminder ? (
            <div className="rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-[#2a210f] p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <Typography variant="subtitle2" className="text-amber-900 dark:text-amber-100 font-semibold">
                  Social & Household Context is incomplete
                </Typography>
                <Typography variant="body2" className="text-amber-800 dark:text-amber-200 mt-1">
                  This citizen has at least two previous completed visits. Complete the household/social context now or defer it; OPD care is not blocked.
                </Typography>
              </div>
              <button
                type="button"
                onClick={goSocialHousehold}
                className="rounded-lg border border-amber-500 px-3 py-2 text-sm font-semibold text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-950 transition-colors"
              >
                Complete context
              </button>
            </div>
          ) : null}
        </div>

        <SectionGrid
          sectionKey="citizen"
          items={citizenItems.filter((item) => item.path !== '__search__')}
          persist={(next) => {
            const full = [citizenItems.find((i) => i.path === '__search__'), ...next].filter(Boolean);
            persistCitizen(full);
          }}
          renderItem={(item, dragClass) => (
            <SortableCard
              key={item.path}
              item={item}
              dragClass={dragClass}
              onClick={citizenAction(item)}
              disabled={citizenDisabled(item)}
            />
          )}
        />
      </section>

      <section className="space-y-4">
        <div>
          <Typography variant="h6" className={`${SECTION_META.activities.accent} font-semibold`}>
            {SECTION_META.activities.title} / {SECTION_META.activities.titleHi}
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-gray-300 mt-1">
            {SECTION_META.activities.description}
          </Typography>
        </div>
        <SectionGrid
          sectionKey="activities"
          items={activityItems}
          persist={persistActivities}
          renderItem={(item, dragClass) => (
            <SortableCard
              key={item.path}
              item={item}
              dragClass={dragClass}
              onClick={activityAction(item)}
            />
          )}
        />
      </section>

      <section className="space-y-4">
        <div>
          <Typography variant="h6" className={`${SECTION_META.telemedicine.accent} font-semibold`}>
            {SECTION_META.telemedicine.title} / {SECTION_META.telemedicine.titleHi}
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-gray-300 mt-1">
            {SECTION_META.telemedicine.description}
          </Typography>
        </div>
        <SectionGrid
          sectionKey="telemedicine"
          items={telemedicineItems}
          persist={persistTelemedicine}
          renderItem={(item, dragClass) => (
            <SortableCard key={item.path} item={item} dragClass={dragClass} />
          )}
        />
      </section>

      <section className="space-y-4">
        <div>
          <Typography variant="h6" className={`${SECTION_META.operations.accent} font-semibold`}>
            {SECTION_META.operations.title} / {SECTION_META.operations.titleHi}
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-gray-300 mt-1">
            {SECTION_META.operations.description}
          </Typography>
        </div>
        <SectionGrid
          sectionKey="operations"
          items={operationsItems}
          persist={persistOperations}
          renderItem={(item, dragClass) => (
            <SortableCard key={item.path} item={item} dragClass={dragClass} />
          )}
        />
      </section>
    </div>
  );
}
